import {
  ContactActivityTypes, ContactTextMessageSendDetail,
  EmailContactActivity,
  FilledTemplateSubjectAndBody,
  hasValue, LinkType, MessageTemplates,
  SimpleEmailMessage, SimpleTextMessage
} from "@amsanalytics/react-shared-library";
import {fetchData, postData} from "../Data/fetch";
import {IEmployee} from "../Interfaces/Admin/IEmployee";
import {IApplication} from "../Interfaces/Application/IApplication";
import {ApplicationDispatch, SET_SERVER_MESSAGE} from "../store/application/types";

// Gets the customer portal link for the contact opportunity.
export const generateCustomerPortalLink = async (
  contactOpportunityId: string, 
  linkType: LinkType,
  dispatch: ApplicationDispatch
): Promise<{portalLink: string} | {error: string}> => {
  // if the link does not exist, create a new one
  const url = `api/contact/portalLink/${contactOpportunityId}/${linkType}`
  const response = await fetchData(url, dispatch);

  // ensure the response is valid before parsing the json
  if (hasValue((response as {error: string}).error)) {
    dispatch({ 
      type: "SET_SERVER_MESSAGE", 
      payload: { message: (response as {error: string}).error, isError: true } });
    return response as {error: string};
  }
  return response as {portalLink: string};
};

// Gets the customer finicity link for the contact opportunity.
export const generateCustomerPortalFinicityLinkUtil = async (
  contactOpportunityId: string,
  linkType: LinkType, 
  dispatch: ApplicationDispatch
): Promise<{portalLink: string} | {error: string}> => {
  // if the link does not exist, create a new one
  const url = `api/finicity/portalLink/${contactOpportunityId}/${linkType}`
  const response = await fetchData(url, dispatch);

  // ensure the response is valid before parsing the json
  if (hasValue((response as {error: string}).error)) {
    dispatch({ 
      type: "SET_SERVER_MESSAGE", 
      payload: { message: (response as {error: string}).error, isError: true } });
    return response as {error: string};
  }
  return response as {portalLink: string};
};

// Gets the customer portal link for the contact opportunity.
export const getFilledTemplate = async (
  contactOpportunityId: string, 
  templateId: string,
  dispatch: ApplicationDispatch
): Promise<FilledTemplateSubjectAndBody | string> => {
  const url = `api/template/GetAndFillMessageTemplateWithApplicationDataForContactOpportunity?contactOpportunityId=${contactOpportunityId}&templateId=${templateId}`
  const filledTemplateSubjectAndBody: FilledTemplateSubjectAndBody | {error: string} = await fetchData(url, dispatch);

  // If there is an error message to report, show it
  if (hasValue((filledTemplateSubjectAndBody as {error: string}).error)) {
    dispatch({ 
      type: "SET_SERVER_MESSAGE", 
      payload: { message: (filledTemplateSubjectAndBody as {error: string}).error, isError: true } });
    return '';
  }
  return filledTemplateSubjectAndBody as FilledTemplateSubjectAndBody;
};

const createMultipleEmailContactActivities = async (emailContactActivities: EmailContactActivity[], dispatch: ApplicationDispatch) => {
  try {
    const url: string = `api/ContactActivity/emails`;
    const response = await postData(url, emailContactActivities);

    if (response.error) {
      dispatch({
        type: SET_SERVER_MESSAGE,
        payload: { isError: true, message: response.error },
      });
    } else {
      dispatch({
        type: SET_SERVER_MESSAGE,
        payload: { isError: false, message: "Emails sent successfully" },
      });
    }
  } catch (err) {
    dispatch({
      type: SET_SERVER_MESSAGE,
      payload: { isError: true, message: err as string },
    });
    return { success: false, newApplicationId: undefined };
  }  
}

const sendMultipleTextMessagesToContact = async (textMessages: ContactTextMessageSendDetail[], dispatch: ApplicationDispatch) => {
  try {
    const url: string = `api/notification/sendContactTextMessages`;
    const response = await postData(url, textMessages);
  
    if (response.error) {
      dispatch({
        type: SET_SERVER_MESSAGE,
        payload: { isError: true, message: response.error },
      });
    } else {
      dispatch({
        type: SET_SERVER_MESSAGE,
        payload: { isError: false, message: "Text messages sent successfully" },
      });
    }
  } catch (err) {
    dispatch({
      type: SET_SERVER_MESSAGE,
      payload: { isError: true, message: err as string },
    });
    return { success: false, newApplicationId: undefined };
  }
}

export const sendSimpleEmailMessagesToContact = (
  currentApp: IApplication, 
  emailMessage: SimpleEmailMessage,
  dispatch: ApplicationDispatch,
  closeDialog: () => void
) => {
  const contactEmailMessages: EmailContactActivity[] = [];

  emailMessage.emailAddresses.forEach(emailAddress => {
    contactEmailMessages.push({
      contactId: currentApp!.contactId!,
      subject: emailMessage.subject,
      body: emailMessage.body,
      toAddress: emailAddress,
      details: `Sending email to: ${emailAddress}`,
      occurredAt: new Date(),
      typeId: ContactActivityTypes.emailContactActivityTypeId,
      successfullySent: true,
      templateId: MessageTemplates.customerPortalLinkEmail,
      title: 'Portal Link Email'
    });
  });

  createMultipleEmailContactActivities(contactEmailMessages, dispatch);
  closeDialog();
}

export const sendSimpleTextMessagesToContact = (
  currentApp: IApplication, 
  textMessage: SimpleTextMessage, 
  currentEmployee: IEmployee | undefined,
  dispatch: ApplicationDispatch,
  closeDialog: () => void
  ) => {
  if (!hasValue(currentEmployee?.id)) {
    return;
  }

  const contactTextMessages: ContactTextMessageSendDetail[] = [];

  textMessage.phoneNumbers.forEach(phoneNumber => {
    contactTextMessages.push({
      message: textMessage.message,
      phoneNumber: phoneNumber,
      employeeId: currentEmployee!.id,
      contactId: currentApp.contactId!,
      mediaUrls: []
    });
  });

  sendMultipleTextMessagesToContact(contactTextMessages, dispatch);
  closeDialog();
}


export const sendFinicityEmailMessagesToContactUtil = async (
  currentApp: IApplication, 
  emailMessage: SimpleEmailMessage,
  dispatch: ApplicationDispatch,
  closeDialog: () => void
) => {
  const contactEmailMessages: EmailContactActivity[] = [];

  emailMessage.emailAddresses.forEach(emailAddress => {
    contactEmailMessages.push({
      contactId: currentApp!.contactId!,
      subject: !emailMessage.subject ? 'Finicity Bank Authorization' : emailMessage.subject,
      body: emailMessage.body,
      toAddress: emailAddress,
      details: `Sending email to: ${emailAddress}`,
      occurredAt: new Date(),
      typeId: ContactActivityTypes.emailContactActivityTypeId,
      successfullySent: true,
      templateId: MessageTemplates.customerPortalLinkEmail,
      title: 'Finicity Bank Authorization'
    });
  });

  // call the api to generate the Finicity link and save it
  try {
    const portalLink = emailMessage.body;
    const guidRegex = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/g; 
    const match = portalLink.match(guidRegex); 
    const linkGuid =  match ? match[0] : null;
    const url = `api/finicity/createFinicityLink/${currentApp.contactId!}/${linkGuid}`
    const response = await fetchData(url, dispatch);  
    
    createMultipleEmailContactActivities(contactEmailMessages, dispatch);
  }

catch (error: any) {
  console.log('Finicty Error: ', error);
}
closeDialog();
}
export const sendFinicityTextMessagesToContactUtil = async (
  currentApp: IApplication, 
  textMessage: SimpleTextMessage, 
  currentEmployee: IEmployee | undefined,
  dispatch: ApplicationDispatch,
  closeDialog: () => void
  ) => {
  if (!hasValue(currentEmployee?.id)) {
    return;
  }

  const contactTextMessages: ContactTextMessageSendDetail[] = [];

  textMessage.phoneNumbers.forEach(phoneNumber => {
    contactTextMessages.push({
      message: textMessage.message,
      phoneNumber: phoneNumber,
      employeeId: currentEmployee!.id,
      contactId: currentApp.contactId!,
      mediaUrls: []
    });
  });
 
  // call the api to generate the Finicity link and save it
  try {
      const portalLink = textMessage.message;
      const guidRegex = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/g; 
      const match = portalLink.match(guidRegex); 
      const linkGuid =  match ? match[0] : null;
      const url = `api/finicity/createFinicityLink/${currentApp.contactId!}/${linkGuid}`
      const response = await fetchData(url, dispatch);  
      
      sendMultipleTextMessagesToContact(contactTextMessages, dispatch);
    }
  
  catch (error: any) {
    console.log('Finicty Error: ', error);
  }
  closeDialog();
}

export const sendDocUploadEmailUtil = async (
  currentApp: IApplication,
  emailMessage: SimpleEmailMessage,
  dispatch: ApplicationDispatch,
  closeDialog: () => void
) => {
  const contactEmailMessages: EmailContactActivity[] = [];

  emailMessage.emailAddresses.forEach(emailAddress => {
    contactEmailMessages.push({
      contactId: currentApp!.contactId!,
      subject: !emailMessage.subject ? 'Insurance Document Upload' : emailMessage.subject,
      body: emailMessage.body,
      toAddress: emailAddress,
      details: `Sending email to: ${emailAddress}`,
      occurredAt: new Date(),
      typeId: ContactActivityTypes.emailContactActivityTypeId,
      successfullySent: true,
      templateId: MessageTemplates.customerPortalLinkEmail,
      title: 'CheckMyDriver Document Upload Link'
    });
  });

  try {
    createMultipleEmailContactActivities(contactEmailMessages, dispatch);
  } catch (error: any) {
    console.log('Document Upload Email Error: ', error);
  }
  closeDialog();
}

export const sendDocUploadTextUtil = async (
  currentApp: IApplication,
  textMessage: SimpleTextMessage,
  currentEmployee: IEmployee | undefined,
  dispatch: ApplicationDispatch,
  closeDialog: () => void
) => {
  if (!hasValue(currentEmployee?.id)) {
    return;
  }

  const contactTextMessages: ContactTextMessageSendDetail[] = [];

  textMessage.phoneNumbers.forEach(phoneNumber => {
    contactTextMessages.push({
      message: textMessage.message,
      phoneNumber: phoneNumber,
      employeeId: currentEmployee!.id,
      contactId: currentApp.contactId!,
      mediaUrls: []
    });
  });

  try {
    sendMultipleTextMessagesToContact(contactTextMessages, dispatch);
  } catch (error: any) {
    console.log('Document Upload Text Error: ', error);
  }
  closeDialog();
}